import type { TimingGrade, WaveAttempt, WaveJudgement } from './judge/Judgement.js';
import type { Wave } from './wave/Wave.js';

/**
 * Everything the simulation knows, as plain data.
 *
 * Readable by the view, writable only by the simulation. Nothing in here is a
 * scene object, a texture handle or a DOM node, which is what allows the whole
 * layer to run in Node under test and — later — to be snapshotted for replays.
 */
export interface SimulationState {
  /** Song position the state was last advanced to, from `MusicClock`. */
  songTime: number;
  /** The wave pool. Skip entries whose `active` is false. */
  readonly waves: readonly Wave[];
  activeWaves: number;
  /** Chart events handed out so far. */
  consumedEvents: number;
  totalEvents: number;
  spawnedWaves: number;
  retiredWaves: number;
  /** Events that produced no wave because the field was full. */
  droppedWaves: number;
  /** Highest concurrent wave count seen this run. */
  peakSimultaneousWaves: number;
  /** True once every chart event has been consumed. */
  chartExhausted: boolean;

  /** Live waves still awaiting a judgement; the rest are dissolving. */
  pendingWaves: number;
  score: number;
  combo: number;
  maxCombo: number;
  /** How many of each grade this run has produced. */
  readonly judgeCounts: Record<TimingGrade, number>;
  /** Taps on either side of the best moment, for a later results breakdown. */
  earlyCount: number;
  lateCount: number;
  /**
   * Judgements that came from an actual tap, and the sum of their offsets.
   *
   * Accumulated here rather than derived from the event log because the log is
   * bounded — it keeps the last few hundred resolutions for the diagnostics
   * read-out — and an average taken from a truncated tail would be a different
   * number that looked like the same one. A running sum is exact for the whole
   * run at the cost of two fields.
   *
   * Misses are excluded on purpose: a wave nobody touched has no timing, and
   * folding its notional offset in would drag the average toward "late" in
   * proportion to how much of the song was ignored.
   */
  tappedJudgements: number;
  timingOffsetSumMs: number;
  lastJudgement: WaveJudgement | null;
  /** Failed taps this run. A wave may contribute several. */
  badAttempts: number;
  lastAttempt: WaveAttempt | null;
  /** Scheduled but not yet visible, at any wave speed. */
  notYetVisibleWaves: number;
  /** Pool slots in use: visible plus not-yet-visible. */
  pooledWaves: number;
}

export interface SimulationSnapshot {
  readonly songTime: number;
  readonly activeWaves: number;
  readonly consumedEvents: number;
  readonly totalEvents: number;
  readonly spawnedWaves: number;
  readonly retiredWaves: number;
  readonly droppedWaves: number;
  readonly peakSimultaneousWaves: number;
  readonly chartExhausted: boolean;
  readonly pendingWaves: number;
  readonly score: number;
  readonly combo: number;
  readonly maxCombo: number;
  readonly judgeCounts: Readonly<Record<TimingGrade, number>>;
  readonly earlyCount: number;
  readonly lateCount: number;
  readonly tappedJudgements: number;
  readonly timingOffsetSumMs: number;
  readonly lastJudgement: WaveJudgement | null;
  readonly badAttempts: number;
  readonly lastAttempt: WaveAttempt | null;
  readonly notYetVisibleWaves: number;
  readonly pooledWaves: number;
}

export function snapshot(state: SimulationState): SimulationSnapshot {
  return {
    songTime: state.songTime,
    activeWaves: state.activeWaves,
    pendingWaves: state.pendingWaves,
    score: state.score,
    combo: state.combo,
    maxCombo: state.maxCombo,
    // Copied, not aliased: the live record keeps mutating and a snapshot that
    // changed under its reader would not be one.
    judgeCounts: { ...state.judgeCounts },
    earlyCount: state.earlyCount,
    lateCount: state.lateCount,
    tappedJudgements: state.tappedJudgements,
    timingOffsetSumMs: state.timingOffsetSumMs,
    lastJudgement: state.lastJudgement,
    badAttempts: state.badAttempts,
    lastAttempt: state.lastAttempt,
    notYetVisibleWaves: state.notYetVisibleWaves,
    pooledWaves: state.pooledWaves,
    consumedEvents: state.consumedEvents,
    totalEvents: state.totalEvents,
    spawnedWaves: state.spawnedWaves,
    retiredWaves: state.retiredWaves,
    droppedWaves: state.droppedWaves,
    peakSimultaneousWaves: state.peakSimultaneousWaves,
    chartExhausted: state.chartExhausted,
  };
}
